import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Dispatch, useCallback, useEffect} from "react";
import {showToast} from "common";
import _ from "lodash";
import {FormProvider, useForm} from "react-hook-form";
import {ProviderMetricsPageAction} from "./ProviderMetricsPage";
import {ReportModal} from "../form-components/ReportModal";
import {LoadingSpinner} from "common";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {ModalMultiSelectionInput} from "../form-components/ModalMultiSelectionInput";
import {ModalDatePicker} from "../form-components/ModalDatePicker";
import {ModalTextAreaInput} from "../form-components/ModalTextAreaInput";
import {ProviderMetricsReportTypeMapNoDefault, ReportInquiringAboutTypeMapNoDefault} from "../domain";
import {validateYear} from "common/util/date";

export type SubmitProviderMetricsInquiryInput = {
    reportTypes: string[];
    reportPeriod: string;
    reportInquiringAbout: string[];
    requestedSupport: string;
}

type SubmitInquiryModalProps = {
    visible: boolean;
    onFinished: (formValues: SubmitProviderMetricsInquiryInput) => Promise<void>;
    dispatch: Dispatch<ProviderMetricsPageAction>;
}

export function SubmitInquiryModal({visible, onFinished, dispatch}: SubmitInquiryModalProps) {
    const methods = useForm<SubmitProviderMetricsInquiryInput>({
        defaultValues: {
            reportTypes: [],
            reportPeriod: "",
            reportInquiringAbout: [],
            requestedSupport: "",
        }
    });
    const selectedReportTypes = methods.watch("reportTypes");
    const shouldShowReportInquiringAbout = selectedReportTypes?.includes("Provider_Performance_Metrics");

    useEffect(() => {
        if (!shouldShowReportInquiringAbout) {
            methods.setValue("reportInquiringAbout", []);
            methods.clearErrors("reportInquiringAbout");
        }
    }, [methods, shouldShowReportInquiringAbout]);

    const submitWithTracking = useCallback((formValues: SubmitProviderMetricsInquiryInput) => trackPromise(onFinished(formValues)), [onFinished]);
    const {promiseInProgress} = usePromiseTracker();

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: SubmitProviderMetricsInquiryInput) => {
            await submitWithTracking(_.pick(data, [
                'reportTypes',
                'reportPeriod',
                'reportInquiringAbout',
                'requestedSupport',
            ]));
            methods.reset();
            dispatch({type: 'HIDE_SUBMIT_INQUIRY_MODAL'});
        })(e);
    }, [methods, dispatch, submitWithTracking]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Provider Data inquiry is cancelled`);
        methods.reset();
        dispatch({type: 'HIDE_SUBMIT_INQUIRY_MODAL'});
    }, [methods, dispatch]);


    return <ReportModal visible={visible || promiseInProgress}
                        title={"Provider Data Inquiry"}
                        maxWidth="max-w-5xl"
                        onClose={onCancel}>
        <FormProvider {...methods}>
            {!promiseInProgress &&
                <form autoComplete={'off'} onSubmit={submit}>
                    <div className="p-4">
                        <ModalMultiSelectionInput<SubmitProviderMetricsInquiryInput>
                            label="Provider Report Type"
                            disabled={promiseInProgress}
                            field="reportTypes"
                            options={Array.from(ProviderMetricsReportTypeMapNoDefault.keys())}
                            required={true}
                            labelPosition="top"
                            labelMapperFunc={(val) => ProviderMetricsReportTypeMapNoDefault.get(val)!}
                            optionalRules={{
                                required: {
                                    value: true,
                                    message: 'Required'
                                }
                            }}
                        />
                        
                        <ModalDatePicker<SubmitProviderMetricsInquiryInput>
                            label="Report Period"
                            disabled={promiseInProgress}
                            field="reportPeriod"
                            required={true}
                            labelPosition="top"
                            showMonthYearPicker
                            dateFormat={"MMMM/yyyy"}
                            placeholder="Select month and year"
                            maxDate={new Date()}
                            optionalRules={{
                                required: {
                                    value: true,
                                    message: 'Required'
                                },
                                validate: validateYear(true)
                            }}
                        />
                        
                        <>
                            {shouldShowReportInquiringAbout && (
                            <ModalMultiSelectionInput<SubmitProviderMetricsInquiryInput>
                                label="Which metric(s) are you inquiring about?"
                                disabled={promiseInProgress}
                                field="reportInquiringAbout"
                                options={Array.from(ReportInquiringAboutTypeMapNoDefault.keys())}
                                labelMapperFunc={(val) => ReportInquiringAboutTypeMapNoDefault.get(val)!}
                                required={true}
                                labelPosition="top"
                                optionalRules={{
                                    required: {
                                        value: true,
                                        message: 'Please select at least 1 value from the "Which metric(s) are you inquiring about?" field.'
                                    }
                                }}
                            />
                            )}
                            
                            <ModalTextAreaInput<SubmitProviderMetricsInquiryInput>
                                label="How can we assist?"
                                disabled={promiseInProgress}
                                field="requestedSupport"
                                required={true}
                                rowsNum={4}
                                maxLength={4000}
                                labelPosition="top"
                                showCharacterCount
                                shouldStopInputOnMaxLength={false}
                                requiredMessage="Please provide the details of your inquiry."
                            />
                            </>
                        

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className={"flex w-1/4"}>
                                <FatOutlineButton color={'red'}
                                                  text={'Cancel'}
                                                  onClick={onCancel}/>
                                <span className={"w-3"}/>
                                <FatOutlineButton text={"Submit"} color={'green'} type={"submit"}/>
                            </div>
                        </div>
                    </div>
                </form>
            }
            {promiseInProgress &&
                <div className="p-24">
                    <LoadingSpinner text="Processing"/>
                </div>
            }
        </FormProvider>
    </ReportModal>;
}